import { memo, useState } from "react";
import * as EmailValidator from "email-validator";
import {
  Alert,
  Box,
  Typography,
  Button,
  Paper,
} from "@mui/material";
import LoadingComponent from "../components/LoadingComponent";
import InfoRow from "../components/InfoRow";
import SecurityCodeModal from "../components/SecurityCodeModal";
import ChangePasswordModal from "../components/ChangePasswordModal";
import EditableNameField from "../components/users/EditableNameField";
import EditableField from "../components/users/EditableField";
import EditableEmailTagsField from "../components/users/EditableEmailTagsField";
import { useUser } from "../hooks/useUser";
import { usePlan } from "../hooks/usePlan";
import { useAuth } from "../hooks/useAuth";

const MyAccount = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isVerifyingCode, setIsVerifyingCode] = useState(false);
  const [pendingAction, setPendingAction] = useState(null);

  const [isChangePasswordModalOpen, setIsChangePasswordModalOpen] =
    useState(false);
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [passwordChangeToken, setPasswordChangeToken] = useState(null);

  const {
    user,
    loading,
    error,
    setError,
    updateName,
    updateEmail,
    requestSecureCode,
    changePassword,
    verifySecureCode,
    updateOperativeMails,
  } = useUser();

  const { plan } = usePlan();
  const { user: authUser } = useAuth();

  const handleChangePasswordClick = async () => {
    await requestSecureCode();
    setPendingAction("changePassword");
    setIsModalOpen(true);
  };

  const handleConfirmCode = async (code) => {
    setIsVerifyingCode(true);
    try {
      const response = await verifySecureCode(code);
      if (response.valid) {
        if (pendingAction === "changePassword") {
          setPasswordChangeToken(response.token);
          setIsChangePasswordModalOpen(true);
        }
      } else {
        setError("Código incorrecto. Inténtalo de nuevo.");
      }
    } catch (err) {
      setError("Ocurrió un error al verificar el código.");
    } finally {
      setIsModalOpen(false);
      setIsVerifyingCode(false);
    }
  };

  const handlePasswordChangeConfirm = async (newPassword) => {
    setIsChangingPassword(true);
    try {
      await changePassword(newPassword, passwordChangeToken);
      setIsChangePasswordModalOpen(false);
      setPasswordChangeToken(null);
    } catch (err) {
      setError("Ocurrió un error al cambiar la contraseña.");
    } finally {
      setIsChangingPassword(false);
    }
  };

  const validateEmail = (email) => {
    return !EmailValidator.validate(email);
  };

  return (
    <>
      {loading ? <LoadingComponent /> : null}
      {error ? (
        <Alert sx={{ borderRadius: "0px !important" }} severity="error">
          {error}
        </Alert>
      ) : null}
      {user ? (
        <>
          <Box sx={{ p: 3 }}>
            <Typography variant="h5" sx={{ mb: 1 }} id={"title-my-account"}>
              Hola, {user.full_name}
            </Typography>

            <Paper
              elevation={0}
              sx={{ pt: 3, backgroundColor: "#fff", maxWidth: "700px" }}
            >
              <Typography variant="h5" id={"subtitle-my-account"}>
                Mi información de cuenta
              </Typography>
              <InfoRow label="Mi plan">
                <Typography variant="body1">{authUser?.admin_plan?.plan_name || "Sin información"}</Typography>
              </InfoRow>

              <InfoRow label="Nombre">
                <EditableNameField
                  initialFirstName={user.first_name}
                  initialLastName={user.last_name}
                  onSave={updateName}
                />
              </InfoRow>

              <InfoRow label="RUT">
                <Typography variant="body1">{user.rut}</Typography>
              </InfoRow>

              <InfoRow label="User">
                <Typography variant="body1">{user.username}</Typography>
              </InfoRow>

              <InfoRow label="Correo">
                <EditableField
                  initialValue={user.email}
                  onSave={(newEmail) => updateEmail({ email: newEmail })}
                  validate={validateEmail}
                />
              </InfoRow>

              <InfoRow label="Correos de reporteria">
                <EditableEmailTagsField
                  initialEmails={user.operative_mails}
                  onSave={updateOperativeMails}
                />
              </InfoRow>

              <InfoRow label="Contraseña">
                <Button
                  size="small"
                  variant="text"
                  sx={{
                    borderRadius: "0px !important",
                    fontWeight: "bolder !important",
                    fontSize: "1rem !important"
                  }}
                  onClick={handleChangePasswordClick}
                >
                  Cambiar contraseña
                </Button>
              </InfoRow>
            </Paper>
          </Box>
          <SecurityCodeModal
            open={isModalOpen}
            onClose={() => setIsModalOpen(false)}
            onConfirm={handleConfirmCode}
            loading={isVerifyingCode}
          />

          <ChangePasswordModal
            open={isChangePasswordModalOpen}
            onClose={() => setIsChangePasswordModalOpen(false)}
            onConfirm={handlePasswordChangeConfirm}
            loading={isChangingPassword}
          />
        </>
      ) : null}
    </>
  );
};

export default memo(MyAccount);
